import { Link } from 'react-router-dom';

import { Badge, Button } from '@/components/ui';
import { HomeMedia } from '@/components/home/HomeMedia';
import { formatCOP } from '@/lib/format';
import {
  experienceEmoji,
  EXPERIENCE_CATEGORY_LABELS,
  type Experience,
  type ExperienceCategory,
} from '@/hooks/useExperiences';

/**
 * HomeExperienceCard — card de Experiencia VIP para el landing. Local al Home
 * (no toca `/experiences`). Usa la imagen real (`imageUrl`) vía `HomeMedia`;
 * cuando no hay imagen, el fallback de marca muestra el emoji de la categoría
 * (recurso visual ya existente en el hook). Datos, ruta y precio intactos.
 */

function categoryLabel(category: string): string | null {
  return EXPERIENCE_CATEGORY_LABELS[category as ExperienceCategory] ?? null;
}

export function HomeExperienceCard({ experience }: { experience: Experience }): JSX.Element {
  const label = categoryLabel(experience.category);

  return (
    <div className="group flex h-full flex-col overflow-hidden rounded-card border border-fc-line bg-fc-paper shadow-sm transition duration-200 hover:-translate-y-1 hover:border-primary-200 hover:shadow-lg">
      <HomeMedia
        src={experience.imageUrl}
        alt={experience.title}
        aspect="aspect-[16/10]"
        fallbackIcon={<span className="text-5xl">{experienceEmoji(experience.category)}</span>}
      >
        {label && (
          <Badge tone="gold" className="absolute left-3 top-3 shadow-sm">
            {label}
          </Badge>
        )}
      </HomeMedia>

      <div className="flex flex-1 flex-col p-4 md:p-5">
        <h3 className="line-clamp-2 font-display text-lg font-semibold text-fc-ink">
          {experience.title}
        </h3>
        {experience.subtitle && (
          <p className="mt-1.5 line-clamp-2 text-sm text-fc-muted">{experience.subtitle}</p>
        )}
        {experience.location && (
          <p className="mt-1 line-clamp-1 text-xs text-fc-muted">{experience.location}</p>
        )}
        <div className="mt-auto flex items-center justify-between gap-2 border-t border-fc-line/70 pt-4">
          <span className="font-display text-base font-semibold tabular-nums text-fc-ink">
            {formatCOP(experience.priceCents)}
          </span>
          <Button as={Link} to={`/experiences/${experience.id}`} variant="secondary" size="sm">
            Ver detalle
          </Button>
        </div>
      </div>
    </div>
  );
}
